// Bootstrap exception: the User repository has no Sports feature-unit generator.
// Keep URL construction and eligibility outside the presentation component.

import { isSportsnewLiveFixtureRow } from '../../../_context/_helpers/sportsnewListFixtureVisibility.js';

const YOUTUBE_VIDEO_ID = /^[A-Za-z0-9_-]{6,64}$/;
const TWITCH_CHANNEL_ID = /^[A-Za-z0-9_]{1,25}$/;
const KICK_CHANNEL_ID = /^[A-Za-z0-9_]{2,32}$/;
const HOSTNAME = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)(?:\.(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?))+$/;

const buildEnv = {
  twitchParents: process.env.REACT_APP_SPORTSNEW_TWITCH_PARENT_ALLOWLIST,
  kickEnabled: process.env.REACT_APP_SPORTSNEW_BROADCAST_KICK_EMBED_ENABLED,
  isDevelopment: process.env.NODE_ENV === 'development',
};

const isIpAddress = value => (
  /^\d{1,3}(?:\.\d{1,3}){3}$/.test(value)
  || value.includes(':')
);

const isCanonicalDnsHostname = value => {
  if (!HOSTNAME.test(value) || isIpAddress(value)) return false;
  try {
    const parsedHostname = new URL(`https://${value}`).hostname.toLowerCase();
    return parsedHostname === value && !isIpAddress(parsedHostname);
  } catch {
    return false;
  }
};

export function resolveKickEmbedEnabled(rawValue) {
  return typeof rawValue === 'string' && rawValue === 'true';
}

export function parseTwitchParentAllowlist(rawValue, options = {}) {
  const developmentLocalhostAllowed = options.isDevelopment === true
    && String(options.currentHostname ?? '').toLowerCase() === 'localhost';
  const raw = String(rawValue ?? '').trim();
  if (!raw) return Object.freeze([]);
  const values = raw
    .split(',')
    .map(value => value.trim().toLowerCase());
  const valid = value => (
    value.length > 0
    && (
      (value === 'localhost' && developmentLocalhostAllowed)
      || (
        value !== 'localhost'
        && !value.includes('*')
        && !value.includes('/')
        && !value.includes(':')
        && isCanonicalDnsHostname(value)
      )
    )
  );
  if (!values.every(valid)) return Object.freeze([]);
  return Object.freeze([...new Set(values)]);
}

const DEFAULT_TWITCH_PARENT_ALLOWLIST = parseTwitchParentAllowlist(
  buildEnv.twitchParents,
  {
    isDevelopment: buildEnv.isDevelopment === true,
    currentHostname: typeof window === 'undefined' ? '' : window.location.hostname,
  },
);
const DEFAULT_KICK_ENABLED = resolveKickEmbedEnabled(
  buildEnv.kickEnabled ?? 'true',
);

const validResource = value => {
  if (!value || value.mode !== 'embed') return false;
  if (value.provider === 'youtube') {
    return value.resourceType === 'video' && YOUTUBE_VIDEO_ID.test(String(value.resourceId ?? ''));
  }
  if (value.provider === 'twitch') {
    return value.resourceType === 'channel' && TWITCH_CHANNEL_ID.test(String(value.resourceId ?? ''));
  }
  if (value.provider === 'kick') {
    return value.resourceType === 'channel' && KICK_CHANNEL_ID.test(String(value.resourceId ?? ''));
  }
  return false;
};

export function isSportsBroadcastFixtureEligible({
  activeMatch,
  detailFixture,
  detailMatchesActiveFixture,
  activeMatchPresentInLiveList,
} = {}) {
  if (!activeMatch || !detailFixture || detailMatchesActiveFixture !== true) return false;
  if (activeMatchPresentInLiveList !== true) return false;
  if (activeMatch.sportId !== 'esports' || detailFixture.sport !== 'esports') return false;
  if (!isSportsnewLiveFixtureRow(activeMatch._raw)) return false;
  if (!isSportsnewLiveFixtureRow(detailFixture)) return false;
  return true;
}

export function isSportsBroadcastPanelEligible({
  activeMatch,
  detailFixture,
  detailMatchesActiveFixture,
  activeMatchPresentInLiveList,
  availableResourceCount,
} = {}) {
  return isSportsBroadcastFixtureEligible({
    activeMatch,
    detailFixture,
    detailMatchesActiveFixture,
    activeMatchPresentInLiveList,
  })
    && Number.isInteger(availableResourceCount)
    && availableResourceCount > 0;
}

const providerRank = provider => {
  if (provider === 'youtube') return 1;
  if (provider === 'twitch') return 2;
  if (provider === 'kick') return 3;
  return 99;
};

const providerDisplayName = provider => ({
  youtube: 'YouTube',
  kick: 'Kick',
  twitch: 'Twitch',
})[provider] ?? '';

const validTwitchPlayerSize = (widthValue, heightValue) => {
  const width = Number(widthValue);
  const height = Number(heightValue);
  return Number.isFinite(width)
    && Number.isFinite(height)
    // Admit narrow mobile panels; this is our layout floor, not a provider support guarantee.
    && width >= 200
    && height >= 300;
};

const finitePlayerDimension = value => {
  const dimension = Number(value);
  return Number.isFinite(dimension) && dimension > 0
    ? Math.floor(dimension)
    : 0;
};

export function resolveSportsBroadcastPlayerCapacity({
  playerWidth,
  playerHeight,
  lastPlayingHeight,
  preserveLastPlayingHeight = false,
} = {}) {
  const currentHeight = finitePlayerDimension(playerHeight);
  return Object.freeze({
    playerWidth: finitePlayerDimension(playerWidth),
    playerHeight: preserveLastPlayingHeight
      ? Math.max(currentHeight, finitePlayerDimension(lastPlayingHeight))
      : currentHeight,
  });
}

export function listSportsBroadcastResources(resources = [], options = {}) {
  if (!Array.isArray(resources) || resources.length > 8) return Object.freeze([]);
  const kickEnabled = options.kickEnabled ?? DEFAULT_KICK_ENABLED;
  const twitchParentAllowlist = options.twitchParentAllowlist ?? DEFAULT_TWITCH_PARENT_ALLOWLIST;
  const hostname = String(options.hostname ?? '').toLowerCase();
  const twitchPlayerSizeValid = validTwitchPlayerSize(
    options.playerWidth,
    options.playerHeight,
  );
  const excludedResourceKeys = options.excludedResourceKeys instanceof Set
    ? options.excludedResourceKeys
    : new Set(options.excludedResourceKeys ?? []);
  const seen = new Set();
  const ordered = resources
    .filter(validResource)
    .filter(resource => {
      const key = sportsBroadcastResourceKey(resource);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .filter(resource => !excludedResourceKeys.has(sportsBroadcastResourceKey(resource)))
    .filter(resource => resource.provider !== 'kick' || kickEnabled === true)
    .filter(resource => (
      resource.provider !== 'twitch'
      || (
        twitchPlayerSizeValid
        && twitchParentAllowlist.includes(hostname)
      )
    ))
    .map((resource, sourceIndex) => ({ resource, sourceIndex }))
    .sort((left, right) => (
      providerRank(left.resource.provider) - providerRank(right.resource.provider)
      || left.sourceIndex - right.sourceIndex
    ))
    .map(({ resource }) => resource);
  const providerCounts = ordered.reduce((counts, resource) => {
    counts.set(resource.provider, (counts.get(resource.provider) ?? 0) + 1);
    return counts;
  }, new Map());
  return Object.freeze(ordered.map(resource => {
    const provider = resource.provider;
    const baseLabel = providerDisplayName(provider);
    return Object.freeze({
      ...resource,
      displayLabel: providerCounts.get(provider) > 1
        ? `${baseLabel} · ${resource.resourceId}`
        : baseLabel,
    });
  }));
}

export function selectSportsBroadcastResource(resources = [], options = {}) {
  return listSportsBroadcastResources(resources, options)[0] ?? null;
}

export const sportsBroadcastResourceKey = resource => [
  resource?.provider ?? '',
  resource?.resourceType ?? '',
  resource?.resourceId ?? '',
].join(':');

export function buildSportsBroadcastEmbed(resource, options = {}) {
  if (!validResource(resource)) return null;
  const resourceId = String(resource.resourceId);
  if (resource.provider === 'youtube') {
    return Object.freeze({
      provider: 'youtube',
      title: `${resource.label || 'YouTube'} 경기 방송`,
      src: `https://www.youtube.com/embed/${encodeURIComponent(resourceId)}?autoplay=0`,
      allow: 'encrypted-media; picture-in-picture',
    });
  }
  if (resource.provider === 'twitch') {
    const hostname = String(options.hostname ?? '').toLowerCase();
    const parents = options.twitchParentAllowlist ?? DEFAULT_TWITCH_PARENT_ALLOWLIST;
    if (!parents.includes(hostname)) return null;
    if (!validTwitchPlayerSize(options.playerWidth, options.playerHeight)) return null;
    const query = new URLSearchParams({
      channel: resourceId,
      parent: hostname,
      autoplay: 'false',
      muted: 'true',
    });
    return Object.freeze({
      provider: 'twitch',
      title: `${resource.label || 'Twitch'} 경기 방송`,
      src: `https://player.twitch.tv/?${query.toString()}`,
      allow: 'encrypted-media; picture-in-picture',
    });
  }
  if (resource.provider === 'kick' && (options.kickEnabled ?? DEFAULT_KICK_ENABLED) === true) {
    return Object.freeze({
      provider: 'kick',
      title: `${resource.label || 'Kick'} 경기 방송`,
      src: `https://player.kick.com/${encodeURIComponent(resourceId)}?autoplay=false`,
      allow: 'encrypted-media; picture-in-picture',
    });
  }
  return null;
}

export const SPORTS_BROADCAST_DEFAULTS = Object.freeze({
  kickEnabled: DEFAULT_KICK_ENABLED,
  twitchParentAllowlist: DEFAULT_TWITCH_PARENT_ALLOWLIST,
});
